import { CheckCircle2 } from "lucide-react";
import { FeedbackRichText } from "@/components/feedback/FeedbackRichText";
import {
  FeedbackThumbGrid,
  formatFeedbackDate,
} from "@/components/feedback/feedback-ui";

const TECH_DELIMITER = "<!-- TECHNICAL_AUDIT_TRAIL -->";

/**
 * Chuẩn hóa tên kỹ sư phụ trách: triệt tiêu hoàn toàn chuỗi CUID/UUID kỹ thuật
 */
export function formatEngineerDisplayName(raw?: string | null): string {
  if (!raw || !raw.trim() || raw === "N/A") {
    return "Nguyễn Tấn Hiền (Team Dev)";
  }
  const trimmed = raw.trim();
  if (
    trimmed === "cmlfbepxf0000tmitr99xnm1v" ||
    trimmed.toLowerCase() === "gpems.net@gmail.com" ||
    trimmed.toLowerCase() === "administrator" ||
    trimmed.toLowerCase() === "admin" ||
    /^c[a-z0-9]{20,30}$/i.test(trimmed) ||
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed)
  ) {
    return "Nguyễn Tấn Hiền (Team Dev)";
  }
  return trimmed;
}

export function formatReporterDisplayName(
  reportedByName?: string | null,
  reportedBy?: string | null,
): string {
  const candidate =
    reportedByName && reportedByName !== "N/A" && reportedByName !== reportedBy
      ? reportedByName.trim()
      : (reportedBy || "").trim();

  if (!candidate || candidate === "N/A") {
    return "Người dùng hệ thống";
  }
  if (
    candidate === "cmlfbepxf0000tmitr99xnm1v" ||
    candidate.toLowerCase() === "gpems.net@gmail.com" ||
    candidate.toLowerCase() === "administrator" ||
    candidate.toLowerCase() === "admin"
  ) {
    return "Administrator";
  }
  if (
    /^c[a-z0-9]{20,30}$/i.test(candidate) ||
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(candidate)
  ) {
    return "Người dùng hệ thống";
  }
  return candidate;
}

/** Chỉ lấy phần phản hồi dành cho người dùng cuối (bỏ mọi phần kỹ thuật cũ) */
export function extractUserFacingResponse(devResponse?: string | null): string {
  if (!devResponse || !devResponse.trim()) return "";
  if (devResponse.includes(TECH_DELIMITER)) {
    return devResponse.split(TECH_DELIMITER)[0]?.trim() || "";
  }
  if (devResponse.includes("### ")) {
    const idx = devResponse.indexOf("### ");
    return idx > 0 ? devResponse.substring(0, idx).trim() : "Đã kiểm tra và khắc phục xong vấn đề này.";
  }
  return devResponse.trim();
}

interface FeedbackResolutionResultProps {
  resolutionNote?: string | null;
  beforeImages: string[];
  afterImages: string[];
  resolvedBy?: string | null;
  resolvedAt?: string | Date | null;
  onZoomImage?: (url: string) => void;
}

/**
 * Khối "Kết quả xử lý" (chuẩn EMS 4.3): hiển thị khi phiếu Hoàn thành / Đã đóng —
 * ghi chú kết quả ngắn gọn + so sánh ảnh trước / sau.
 */
export function FeedbackResolutionResult({
  resolutionNote,
  beforeImages,
  afterImages,
  resolvedBy,
  resolvedAt,
  onZoomImage,
}: FeedbackResolutionResultProps) {
  const note = extractUserFacingResponse(resolutionNote);
  return (
    <section
      data-testid="fb-resolution-result"
      className="bg-white rounded-xl border border-emerald-200 shadow-sm overflow-hidden"
    >
      <div className="px-4 sm:px-5 py-3 border-b border-emerald-100 bg-emerald-50/70 flex flex-wrap items-center gap-2">
        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
        <h3 className="text-base font-bold text-emerald-900">Kết quả xử lý</h3>
        {(resolvedBy || resolvedAt) && (
          <span className="ml-auto text-xs text-emerald-800">
            {resolvedBy ? formatEngineerDisplayName(resolvedBy) : ""}
            {resolvedBy && resolvedAt ? " • " : ""}
            {resolvedAt ? formatFeedbackDate(resolvedAt) : ""}
          </span>
        )}
      </div>
      <div className="p-4 sm:p-5 space-y-4">
        <FeedbackRichText data-testid="fb-resolution-note" text={note} fallback="Đã xử lý xong." className="text-slate-800" />
        {(beforeImages.length > 0 || afterImages.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-600 mb-2">Trước</div>
              {beforeImages.length ? (
                <FeedbackThumbGrid images={beforeImages} onZoom={onZoomImage} className="sm:grid-cols-2" />
              ) : (
                <p className="text-sm text-slate-400">Không có ảnh</p>
              )}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-emerald-700 mb-2">Sau khi sửa</div>
              {afterImages.length ? (
                <FeedbackThumbGrid
                  testId="fb-after-images"
                  images={afterImages}
                  onZoom={onZoomImage}
                  className="sm:grid-cols-2"
                />
              ) : (
                <p className="text-sm text-slate-400">Không có ảnh</p>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
